const QUESTION_LABEL_KEYS = [
  'question_text',
  'question',
  'text',
  'name',
];

function to_trimmed_string(value) {
  const normalized_value = String(value || '').trim();
  return normalized_value;
}

function is_present_string(value) {
  const has_value = Boolean(to_trimmed_string(value));
  return has_value;
}

function get_question_id_candidates(question) {
  const candidates = [
    question?.section_question_id,
    question?.form_section_question_id,
    question?.section_question,
    question?.id,
    question?.question_id,
  ];
  return candidates;
}

function get_manual_question_id(item) {
  const manual_question_id = item?.manual_verification_section_question_id
    ?? item?.manual_verification_question_id
    ?? item?.manual_question_id
    ?? item?.verification_section_question_id
    ?? item?.verification_question_id
    ?? item?.manual_verification_id;
  return manual_question_id;
}

function get_onboarding_question_id(item) {
  const onboarding_question_id = item?.onboarding_section_question_id
    ?? item?.onboarding_question_id
    ?? item?.onboarding_id;
  return onboarding_question_id;
}

function map_object_entries(source = {}) {
  const next = {};

  Object.keys(source).forEach((key) => {
    const value = Reflect.get(source, key);
    if (!value) {
      return;
    }
    Reflect.set(next, String(key), String(value));
  });

  return next;
}

export function build_empty_state_config(type, form_name, action_event) {
  const trimmed_name = to_trimmed_string(form_name);
  const safe_type = to_trimmed_string(type) || 'form';
  const description = trimmed_name
    ? `The ${trimmed_name} ${safe_type} form has no questions yet. Add questions to that form before mapping.`
    : `This ${safe_type} form has no questions yet. Add questions to that form before mapping.`;
  const empty_state = {
    title: `No ${safe_type} questions found`,
    description,
    action_label: `Go to ${safe_type} form`,
    action_event,
  };
  return empty_state;
}

export function resolve_empty_state({
  loading = false,
  manual_verification_questions = [],
  onboarding_questions = [],
  manual_verification_form_name = '',
  onboarding_form_name = '',
} = {}) {
  if (loading) {
    const empty_state = null;
    return empty_state;
  }

  if (!(Array.isArray(manual_verification_questions) && manual_verification_questions.length)) {
    const manual_state = build_empty_state_config(
      'manual verification',
      manual_verification_form_name,
      'go_to_manual_verification_form',
    );
    return manual_state;
  }

  if (!(Array.isArray(onboarding_questions) && onboarding_questions.length)) {
    const onboarding_state = build_empty_state_config(
      'onboarding',
      onboarding_form_name,
      'go_to_onboarding_form',
    );
    return onboarding_state;
  }

  const empty_state = null;
  return empty_state;
}

export function get_empty_state_route(action_event, form_ids = {}, form_names = {}) {
  const route_map = {
    go_to_manual_verification_form: {
      form_id: Number(form_ids.manual_verification_builder_form_id) || null,
      form_name: to_trimmed_string(form_names.manual_verification_form_name),
    },
    go_to_onboarding_form: {
      form_id: Number(form_ids.onboarding_form_id) || null,
      form_name: to_trimmed_string(form_names.onboarding_form_name),
    },
  };
  const route_config = Reflect.get(route_map, action_event) || null;
  return route_config;
}

export function parse_mapping_response_data(response) {
  const data = response && response.data != null ? response.data : {};
  const parsed_data = typeof data === 'object' && data !== null ? data : {};
  return parsed_data;
}

export function normalize_initial_mappings(source) {
  if (Array.isArray(source)) {
    const next = {};

    source.forEach((item) => {
      const onboarding_id = get_onboarding_question_id(item);
      const manual_id = get_manual_question_id(item);

      if (!onboarding_id || !manual_id) {
        return;
      }
      Reflect.set(next, String(onboarding_id), String(manual_id));
    });

    return next;
  }

  if (source && typeof source === 'object') {
    const normalized_object = map_object_entries(source);
    return normalized_object;
  }

  const empty_mappings = {};
  return empty_mappings;
}

export function get_manual_question_id_from_row(row) {
  const manual_question_id = row?.manual_section_question_id
    ?? row?.manual_question_id
    ?? row?.manual_section_question
    ?? null;
  return manual_question_id;
}

export function get_onboarding_question_id_from_row(row) {
  const onboarding_question_id = row?.onboarding_section_question_id
    ?? row?.onboarding_question_id
    ?? row?.onboarding_section_question
    ?? null;
  return onboarding_question_id;
}

export function apply_onboarding_selection_change(mappings = {}, manual_question_id, onboarding_question_id_raw) {
  const manual_question_id_string = to_trimmed_string(manual_question_id);
  if (!manual_question_id_string) {
    const unchanged_mappings = mappings || {};
    return unchanged_mappings;
  }

  const onboarding_question_id_string = to_trimmed_string(onboarding_question_id_raw);
  const next = { ...(mappings || {}) };

  Object.keys(next).forEach((onboarding_id) => {
    if (String(Reflect.get(next, onboarding_id)) === manual_question_id_string) {
      Reflect.deleteProperty(next, onboarding_id);
    }
  });

  if (!onboarding_question_id_string) {
    return next;
  }

  Reflect.set(next, onboarding_question_id_string, manual_question_id_string);
  return next;
}

export function normalize_question_id_candidate_value(value) {
  if (value == null) {
    const empty_value = null;
    return empty_value;
  }

  if (typeof value !== 'object') {
    return value;
  }

  const id_candidates = [
    value.id,
    value.section_question_id,
    value.question_id,
  ];
  const normalized_value = id_candidates.find((candidate) => candidate != null) ?? null;
  return normalized_value;
}

export function get_question_id_value(question, fallback_id) {
  const candidates = get_question_id_candidates(question);
  const found = candidates
    .map((value) => normalize_question_id_candidate_value(value))
    .find((value) => is_present_string(value));
  const question_id = found != null ? found : fallback_id;
  return question_id;
}

export function get_question_label_value(question = {}) {
  const question_source = question || {};

  for (const key of QUESTION_LABEL_KEYS) {
    const value = Reflect.get(question_source, key);
    if (is_present_string(value)) {
      const label = to_trimmed_string(value);
      return label;
    }
  }

  const empty_label = '';
  return empty_label;
}

export function create_flat_question(question, fallback_id) {
  const label = get_question_label_value(question);
  if (!label) {
    const flat_question = null;
    return flat_question;
  }

  const flat_question = {
    id: get_question_id_value(question, fallback_id),
    label,
  };
  return flat_question;
}

export function flatten_form_structure_items(items) {
  const result = [];
  const main_sections = Array.isArray(items) ? items : [];

  main_sections.forEach((main_section, main_index) => {
    const sub_sections = Array.isArray(main_section?.sub_sections) ? main_section.sub_sections : [];

    sub_sections.forEach((sub_section, sub_index) => {
      const questions = Array.isArray(sub_section?.questions) ? sub_section.questions : [];

      questions.forEach((question, question_index) => {
        const fallback_id = `${main_index}-${sub_index}-${question_index}`;
        const flat_question = create_flat_question(question, fallback_id);
        if (flat_question) {
          result.push(flat_question);
        }
      });
    });
  });

  return result;
}

export function get_reload_form_ids(onboarding_form_id, manual_verification_builder_form_id) {
  const normalized_form_ids = {
    onboarding_form_id: Number(onboarding_form_id) || null,
    manual_verification_builder_form_id: Number(manual_verification_builder_form_id) || null,
  };
  return normalized_form_ids;
}

export function has_required_reload_form_ids(form_ids = {}) {
  const has_required_form_ids = Boolean(
    form_ids.onboarding_form_id && form_ids.manual_verification_builder_form_id,
  );
  return has_required_form_ids;
}

export function build_existing_mappings(items = [], get_onboarding_id, get_manual_id) {
  const next = {};

  items.forEach((item) => {
    if (item?.is_active === false) {
      return;
    }

    const onboarding_id = get_onboarding_id(item);
    const manual_id = get_manual_id(item);
    if (!onboarding_id || !manual_id) {
      return;
    }

    Reflect.set(next, String(onboarding_id), String(manual_id));
  });

  return next;
}

export function build_mapping_payload(mappings = {}) {
  const payload = Object.entries(mappings || {})
    .filter(([, manual_id]) => Boolean(manual_id))
    .map(([onboarding_id, manual_id]) => ({
      onboarding_section_question_id: Number(onboarding_id),
      manual_section_question_id: Number(manual_id),
      is_active: true,
    }))
    .filter((item) => item.onboarding_section_question_id && item.manual_section_question_id);
  return payload;
}
